/**
 * Execution Log Utilities
 *
 * Shared formatting helpers and entry merge functions for audit entry display.
 */

import type {
  AuditEntry,
  AuditEntryGroup,
  BlockedLabelKey,
  BlockedLabels,
  ForcedEndingNote,
  ForcedLabelKey,
  ForcedLabels,
  ToolCallAuditEntry,
  ToolResultAuditEntry,
  DelegationAuditEntry,
  DelegationResultAuditEntry,
  DelegationResultState,
  OrchestratorDecisionAuditEntry,
  TurnAuditEntry,
  HITLRequestAuditEntry,
  NodeCompleteAuditEntry,
} from './types';

/**
 * Resolve how a delegation or agent node ended from the wire's success flag
 * and optional outcome, with the rule streaming-service applies
 * (agion/Agion#2370): blocked only when outcome is "blocked" and success is
 * false. An absent or unknown outcome keeps the success flag's meaning.
 */
export function resolveDelegationState(success: boolean | undefined, outcome?: string): DelegationResultState {
  if (outcome === 'blocked' && !success) return 'blocked';
  return success ? 'succeeded' : 'failed';
}

/**
 * The terminal decision a run's end row records when a person stopped the
 * run (mission-runner pkg/audit missionEndStatusCancelled); governance reads
 * the same value as the run's terminal status. The row's success is false.
 */
export const RUN_END_STOPPED_DECISION = 'cancelled';

/**
 * Whether an audit row is the end of a run a person stopped, as the server
 * recorded it on the end row's decision (agion/Agion#2947). Every console
 * and chat surface reads a stop through this one rule.
 */
export function isStoppedRunEnd(eventType: string | null | undefined, decision: string | null | undefined): boolean {
  return eventType === 'mission_end' && typeof decision === 'string' && decision.trim() === RUN_END_STOPPED_DECISION;
}

/** Whether a node_complete entry is a stopped run's end: marked stopped by its mapper and not succeeded. */
export function isStoppedNode(node: Pick<NodeCompleteAuditEntry, 'success' | 'stopped'>): boolean {
  return node.stopped === true && !node.success;
}

/** English texts of the blocked state; apps pass localized ones as labels. */
export const BLOCKED_LABEL_DEFAULTS: Readonly<Record<BlockedLabelKey, string>> = {
  blocked: 'Blocked by setup',
  blockedTargetNotBound: 'the system is not connected to this mission',
  blockedTargetNotAuthorized: 'this mission is not authorized to use the system',
  blockedOperationOutsideScope: 'the operation is not allowed in this mission',
  blockedCredentialUnavailable: 'the connection has no usable credentials',
  blockedOther: 'the mission setup does not allow this request',
  blockedHint: 'The mission author or an administrator can change this.',
};

/** English body sentence of the blocked state. */
const blockedMessageDefault = (agent: string, reason: string): string =>
  `${agent} could not finish: ${reason}.`;

/** The refusal codes that block a delegation, and the reason each one reads. */
const BLOCKED_REASON_KEYS: ReadonlyMap<string, BlockedLabelKey> = new Map([
  ['target_not_bound', 'blockedTargetNotBound'],
  ['target_not_authorized', 'blockedTargetNotAuthorized'],
  ['operation_outside_scope', 'blockedOperationOutsideScope'],
  ['credential_unavailable', 'blockedCredentialUnavailable'],
]);

/**
 * The text a blocked delegation or node shows: "{agent} could not finish:
 * {phrase}." with the fixed phrase for its refusal code, then who can change
 * the setup. It is built from the agent's name and the code alone, never from
 * the runner's error text. A missing or other code reads the general phrase.
 */
export function blockedReasonText(code: string | undefined, agent: string, labels: BlockedLabels = {}): string {
  const reasonKey = (code && BLOCKED_REASON_KEYS.get(code)) || 'blockedOther';
  const label = (key: BlockedLabelKey) => labels[key] ?? BLOCKED_LABEL_DEFAULTS[key];
  const message = labels.blockedMessage ?? blockedMessageDefault;
  return `${message(agent, label(reasonKey))} ${label('blockedHint')}`;
}

/** English texts of a step forced to finish; apps pass localized ones as labels. */
export const FORCED_LABEL_DEFAULTS: Readonly<Record<ForcedLabelKey, string>> = {
  forced: 'Finished early',
  forcedBudget: 'Finished early: ran out of turns. The result may be incomplete. Narrow what the step asks for, or split it.',
  forcedStuckRepeating: 'Finished early: kept repeating the same call. The result may be incomplete. Change the step\'s instructions.',
  forcedRepeatedFailure: 'Finished early after repeated tool errors. The result may be incomplete. Check the connection the step uses.',
  forcedSchemaChurn: 'Finished early: the source data kept changing shape. The result may be incomplete. Check the source schema.',
};

/**
 * The forced reasons (wire `forced_reason`, the Go list generated into
 * @agion/mission-stream as ForcedReasons) and the warning each one reads.
 */
const FORCED_REASON_KEYS: ReadonlyMap<string, ForcedLabelKey> = new Map([
  ['budget', 'forcedBudget'],
  ['stuck_repeating', 'forcedStuckRepeating'],
  ['repeated_failure', 'forcedRepeatedFailure'],
  ['schema_churn', 'forcedSchemaChurn'],
]);

/**
 * The note of a step the runner forced to finish (agion/Agion#2460): a
 * completed step with a known forced reason passed with valid outputs, but
 * its answer may be incomplete, so it reads as a warning with what to do. A
 * step that did not complete, has no reason, or has a reason this build does
 * not know has no note, so an unknown value never reaches the screen.
 */
export function forcedEndingNote(
  success: boolean | undefined,
  reason: string | undefined,
  labels: ForcedLabels = {},
): ForcedEndingNote | undefined {
  const key = success && reason ? FORCED_REASON_KEYS.get(reason) : undefined;
  if (!key) return undefined;
  const label = (k: ForcedLabelKey) => labels[k] ?? FORCED_LABEL_DEFAULTS[k];
  return { severity: 'warning', label: label('forced'), text: label(key) };
}

/** Format duration in human-readable format */
export function formatDuration(ms: number): string {
  if (ms < 0) return '0ms';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
}

/** Format HITL wait duration — shows "Timed out" for waits exceeding 10 minutes */
export function formatHITLDuration(ms: number): string {
  if (ms > 600_000) return 'Timed out';
  return formatDuration(ms);
}

/** Format token count with thousands abbreviation */
export function formatTokens(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toLocaleString();
}

/** Format time as simple HH:MM */
export function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Merge tool_result entries into their matching tool_call entries.
 * When a tool_result has a toolCallId matching a tool_call, the result data
 * is folded into the call entry and the standalone result entry is removed.
 */
export function mergeToolResults(entries: AuditEntry[]): AuditEntry[] {
  const callMap = new Map<string, ToolCallAuditEntry>();
  const resultIdsToRemove = new Set<string>();

  // First pass: index tool_call entries by toolCallId
  for (const e of entries) {
    if (e.type === 'tool_call') {
      const call = e as ToolCallAuditEntry;
      if (call.toolCallId) callMap.set(call.toolCallId, call);
    }
  }

  // Second pass: merge tool_result data into matching tool_call
  for (const e of entries) {
    if (e.type === 'tool_result') {
      const result = e as ToolResultAuditEntry;
      const call = result.toolCallId ? callMap.get(result.toolCallId) : undefined;
      if (call) {
        call.resolved = true;
        call.resolvedSuccess = result.success;
        call.resolvedOutput = result.output;
        call.resolvedError = result.error;
        call.resolvedDeniedByGuard = result.deniedByGuard;
        call.resolvedDurationMs = result.durationMs;
        if (result.outputFiles && result.outputFiles.length > 0) call.resolvedOutputFiles = result.outputFiles;
        resultIdsToRemove.add(e.id);
      }
    }
  }

  return entries.filter(e => !resultIdsToRemove.has(e.id));
}

/**
 * Merge delegation_result entries into their matching delegation entries.
 * Same pattern as mergeToolResults, keyed on delegationId.
 */
export function mergeDelegationResults(entries: AuditEntry[]): AuditEntry[] {
  const delegationMap = new Map<string, DelegationAuditEntry>();
  const resultIdsToRemove = new Set<string>();

  for (const e of entries) {
    if (e.type === 'delegation') {
      const del = e as DelegationAuditEntry;
      if (del.delegationId) delegationMap.set(del.delegationId, del);
    }
  }

  for (const e of entries) {
    if (e.type === 'delegation_result') {
      const result = e as DelegationResultAuditEntry;
      const del = result.delegationId ? delegationMap.get(result.delegationId) : undefined;
      if (del) {
        del.resolved = true;
        del.resolvedSuccess = result.success;
        del.resolvedSummary = result.summary;
        del.resolvedError = result.error;
        del.resolvedDurationMs = result.durationMs;
        del.resolvedTokensUsed = result.tokensUsed;
        del.resolvedOutcome = result.outcome;
        del.resolvedBlockedReason = result.blockedReason;
        resultIdsToRemove.add(e.id);
      }
    }
  }

  return entries.filter(e => !resultIdsToRemove.has(e.id));
}

/**
 * Filter out low-value noise entries from the audit trail.
 * - Ghost turns with 0 tokens, 0ms duration
 * - Orchestrator decisions with empty/placeholder reasoning
 */
export function filterNoise(entries: AuditEntry[]): AuditEntry[] {
  return entries.filter(e => {
    // Remove ghost turn entries (0 tokens, 0ms, AND no content — truly empty)
    if (e.type === 'turn') {
      const turn = e as TurnAuditEntry;
      if (turn.inputTokens === 0 && turn.outputTokens === 0 && turn.durationMs === 0
          && !turn.userPrompt && !turn.assistantResponse) {
        return false;
      }
    }

    // Remove ALL orchestrator decisions with placeholder reasoning.
    // They add no user value. Cycle boundaries are derived from
    // delegation entries instead.
    if (e.type === 'orchestrator_decision') {
      const dec = e as OrchestratorDecisionAuditEntry;
      const r = dec.reasoning?.trim() ?? '';
      if (!r || r.startsWith('(tool call')) {
        return false;
      }
    }

    // Remove ask_user/synthesize_results tool calls — the hitl_request/hitl_response
    // entries provide the same information with better semantics
    if (e.type === 'tool_call') {
      const call = e as ToolCallAuditEntry;
      if (call.toolName === 'ask_user' || call.toolName === 'synthesize_results') {
        return false;
      }
    }

    return true;
  });
}

/**
 * Group audit entries by conversation turn.
 *
 * One group = one conversation turn:
 *   Agent greeting/question (hitl_request)
 *   → User response (hitl_response)
 *   → Agent work (delegations, tool calls)
 *   → Agent answer (next hitl_request starts the next group)
 *
 * The group summary shows the user's question (from hitl_response)
 * or the delegated agent's name if no user interaction occurred.
 */
export function groupByCycle(entries: AuditEntry[], isExecuting = false): AuditEntryGroup[] {
  if (entries.length === 0) return [];

  const groups: AuditEntryGroup[] = [];
  let currentEntries: AuditEntry[] = [];
  let cycleNumber = 1;

  const flushGroup = () => {
    if (currentEntries.length === 0) return;

    const first = currentEntries[0]!;
    const last = currentEntries[currentEntries.length - 1]!;

    // Determine summary: prefer user's question, then delegation agent name
    let summary = '';
    let status: 'running' | 'completed' | 'failed' = 'completed';

    // Use user's message as the summary (most meaningful for the turn)
    for (const e of currentEntries) {
      if (e.type === 'hitl_response') {
        const resp = e as import('./types').HITLResponseAuditEntry;
        const text = resp.response || '';
        summary = text.length > 50 ? text.slice(0, 47) + '...' : text;
        break;
      }
    }

    // Fallback: delegation agent name
    if (!summary) {
      for (const e of currentEntries) {
        if (e.type === 'delegation') {
          const del = e as DelegationAuditEntry;
          summary = del.agentLabel || del.task;
          if (del.resolved) {
            status = del.resolvedSuccess ? 'completed' : 'failed';
          } else {
            status = isExecuting ? 'running' : 'completed';
          }
          break;
        }
      }
    }

    // Fallback: agent's message (hitl_request prompt)
    if (!summary) {
      for (const e of currentEntries) {
        if (e.type === 'hitl_request') {
          const req = e as HITLRequestAuditEntry;
          const text = req.prompt || '';
          summary = text.length > 50 ? text.slice(0, 47) + '...' : text;
          if (req.pending) status = 'running';
          break;
        }
      }
    }

    if (!summary) summary = `Turn ${cycleNumber}`;

    // Determine status from delegation if not already set
    if (status === 'completed') {
      for (const e of currentEntries) {
        if (e.type === 'delegation') {
          const del = e as DelegationAuditEntry;
          if (del.resolved && !del.resolvedSuccess) status = 'failed';
          if (!del.resolved && isExecuting) status = 'running';
          break;
        }
      }
    }

    // Aggregate tokens
    let totalTokens = 0;
    for (const e of currentEntries) {
      if (e.type === 'turn') {
        totalTokens += (e as TurnAuditEntry).inputTokens + (e as TurnAuditEntry).outputTokens;
      }
      if (e.type === 'orchestrator_decision') {
        totalTokens += (e as OrchestratorDecisionAuditEntry).tokensUsed ?? 0;
      }
      if (e.type === 'delegation') {
        totalTokens += (e as DelegationAuditEntry).resolvedTokensUsed ?? 0;
      }
    }

    groups.push({
      id: `cycle-${cycleNumber}`,
      cycleNumber,
      startTimestamp: first.timestamp,
      endTimestamp: last.timestamp,
      summary,
      status,
      entries: currentEntries,
      durationMs: last.timestamp - first.timestamp,
      totalTokens,
    });

    cycleNumber++;
    currentEntries = [];
  };

  for (const entry of entries) {
    // New turn starts at each hitl_request (agent message to user) —
    // but only if we already have entries (don't flush empty groups)
    if (entry.type === 'hitl_request' && currentEntries.length > 0) {
      flushGroup();
    }
    currentEntries.push(entry);
  }

  // Flush remaining
  if (currentEntries.length > 0) {
    flushGroup();
  }

  if (isExecuting && groups.length > 0) {
    groups[groups.length - 1]!.status = 'running';
  }

  return groups;
}
